import React from 'react';
import PropTypes from 'prop-types';

// Utils
import SectionUtil from 'Utils/content/section-util';
import isIntegerMatch from 'Utils/is-integer-match';

// Components
import NoContentGuard from '../../components/no-content-guard';
import SectionHeader from '../../components/section-header';
import ItemList, { ITEM_LIST_STYLE_MODIFIERS } from '../section/item-list';
import CTABanner, { CTA_BANNER_THEMES } from '../../components/cta-banner';
import LoadMoreScrollToBottomTracker from '../../components/load-more-scroll-to-bottom-tracker';
import VisibilityScrollToBottomTracker from '../../components/visibility-scroll-to-bottom-tracker';

import withT from 'ComponentLibrary/hocs/withT';

import connector from 'Utils/connector';
import connectAmp from 'Webapp/shared/app/connectors/connectAmp';
import connectAuthentication from 'Webapp/shared/app/connectors/connectAuthentication';
import connectRouting from 'Webapp/shared/app/connectors/connectRouting';
import connectCurrentUser from 'Webapp/shared/app/connectors/connectCurrentUser';

function SectionContent({
  section,
  items,
  contributors,
  isLoading,
  isAmp,
  topicDescriptions,
  routing,
  loadNextPage,
  neverLoadMore,
  uid,
  includeHeader,
  usageTrackReachedSectionEnd,
  t,
  currentUserData,
}) {
  const itemsToRender = SectionUtil.itemsToRender(
    section,
    items,
    currentUserData,
  );
  const hasItemsToRender = itemsToRender.length > 0;
  const isPrivateContributorMagazine =
    section.isPrivate && !isIntegerMatch(section.userid, uid);

  return (
    <React.Fragment>
      {includeHeader && (
        <SectionHeader
          section={section}
          topicDescriptions={topicDescriptions}
          path={routing.url}
        />
      )}

      <section className="centered-column">
        {isPrivateContributorMagazine && (
          <p className="section-content__private-message ui-text--supporting">
            {t('this_magazine_is_private')}
          </p>
        )}
        <NoContentGuard hasContent={hasItemsToRender}>
          <ItemList
            section={section}
            items={itemsToRender}
            sectionHasContributors={contributors.length > 0}
            styleModifier={ITEM_LIST_STYLE_MODIFIERS.GRID}
          />
          {loadNextPage && !neverLoadMore && !isAmp && (
            <LoadMoreScrollToBottomTracker
              immediateInfinite
              loadNext={loadNextPage}
              isLoading={isLoading}
              onScrolledToBottom={() => usageTrackReachedSectionEnd(section)}
            />
          )}
          {neverLoadMore && (
            <VisibilityScrollToBottomTracker
              onScrolledToBottom={() => usageTrackReachedSectionEnd(section)}
            />
          )}
          {hasItemsToRender && <CTABanner variant={CTA_BANNER_THEMES.RED} />}
        </NoContentGuard>
      </section>
    </React.Fragment>
  );
}

SectionContent.propTypes = {
  section: PropTypes.object,
  items: PropTypes.array,
  contributors: PropTypes.array,
  topicDescriptions: PropTypes.array,
  routing: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isAmp: PropTypes.bool.isRequired,
  neverLoadMore: PropTypes.bool.isRequired,
  usageTrackReachedSectionEnd: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  loadNextPage: PropTypes.func,
  uid: PropTypes.number.isRequired,
  includeHeader: PropTypes.bool,
  currentUserData: PropTypes.object,
};

SectionContent.defaultProps = {
  section: null,
  items: [],
  topicDescriptions: [],
  contributors: [],
  includeHeader: true,
};

export default connector(
  connectRouting,
  connectAmp,
  connectAuthentication,
  connectCurrentUser,
)(withT(SectionContent));
